import { SectionShell } from "@/components/design-system/SectionShell";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleGroup } from "@/components/ui/collapsible";

const code = `import { Collapsible, CollapsibleGroup } from "@/components/ui/collapsible";

<CollapsibleGroup>
  {sessions.map((session, i) => (
    <Collapsible
      key={session.id}
      defaultOpen={i === 0}
      summary={
        <div className="flex flex-wrap items-baseline gap-x-3">
          <span className="text-sm text-ink-display">14 Aug, 09:12</span>
          <Badge tone="muted">Viewer 2</Badge>
          <span className="text-xs text-ink-muted">6m 40s</span>
        </div>
      }
    >
      <ol>…the detail…</ol>
    </Collapsible>
  ))}
</CollapsibleGroup>`;

export function CollapsibleSection() {
  return (
    <SectionShell
      id="collapsible"
      title="Collapsible"
      description={
        <>
          A disclosure row built on native{" "}
          <code>&lt;details&gt;</code>/<code>&lt;summary&gt;</code>. The browser
          owns the open state, so keyboard handling, disclosure semantics and
          find-in-page all work without JavaScript. Use{" "}
          <code>CollapsibleGroup</code> to stack several so they read as one
          list.
        </>
      }
      whenToUse={
        <ul>
          <li>
            A list where each entry has a scannable summary and a long body —
            per-session detail, per-version history, an FAQ.
          </li>
          <li>
            When showing every body at once would bury the summaries, but hiding
            them behind a page load would be worse.
          </li>
          <li>
            Open the first entry with <code>defaultOpen</code> so the shape of
            the content is visible without a click.
          </li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            One-of-many switching — that is tabs, not a stack of disclosures.
          </li>
          <li>
            Content that must be found by search engines or read by everyone —
            put it on the page.
          </li>
          <li>
            A modal decision or confirmation — use <code>Dialog</code>.
          </li>
        </ul>
      }
      preview={
        <CollapsibleGroup className="max-w-2xl">
          <Collapsible
            defaultOpen
            summary={
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-sm text-ink-display">14 Aug, 09:12</span>
                <Badge tone="accent">Returning</Badge>
                <span className="text-xs text-ink-muted">6m 40s · 5 slides</span>
              </div>
            }
          >
            <p className="text-sm text-ink-body">
              Open by default, so the reader sees what the body looks like
              without having to click anything first.
            </p>
          </Collapsible>

          <Collapsible
            summary={
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-sm text-ink-display">12 Aug, 16:41</span>
                <Badge tone="muted">New viewer</Badge>
                <span className="text-xs text-ink-muted">1m 04s · 2 slides</span>
              </div>
            }
          >
            <p className="text-sm text-ink-body">
              Closed by default. The chevron rotates on open via{" "}
              <code>group-open</code>.
            </p>
          </Collapsible>
        </CollapsibleGroup>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>summary</code> takes any node, so badges and tabular numbers
            can sit in the always-visible row.
          </li>
          <li>
            <code>defaultOpen</code> is uncontrolled — after first render the
            browser owns the state. Nothing needs to hold it in React.
          </li>
          <li>
            Avoid buttons and links inside <code>summary</code>: a click there
            toggles the disclosure as well as firing the control.
          </li>
          <li>
            The native marker is hidden in both Blink and WebKit; the chevron is
            the affordance.
          </li>
        </ul>
      }
    />
  );
}
